import { useTranslation, useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';

export default function Pricing() {
  const { t } = useTranslation();
  const { language } = useAuth();

  const plans = [
    {
      name: t('pricing.free'),
      price: '0',
      desc: t('pricing.freeDesc'),
      features: t('pricing.freeFeatures').split(','),
      popular: false,
      current: true,
    },
    {
      name: t('pricing.family'),
      price: t('pricing.familyPrice'),
      desc: t('pricing.familyDesc'),
      features: t('pricing.familyFeatures').split(','),
      popular: true,
      current: false,
    },
    {
      name: t('pricing.premium'),
      price: t('pricing.premiumPrice'),
      desc: t('pricing.premiumDesc'),
      features: t('pricing.premiumFeatures').split(','),
      popular: false,
      current: false,
    },
  ];

  return (
    <div className="py-16 md:py-24" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#1E293B] mb-4" >
            {t('pricing.title')}
          </h1>
          <p className="text-base md:text-lg text-[#64748B]">{t('pricing.subtitle')}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
          {plans.map((plan, i) => (
            <Card key={i} className={`rounded-2xl transition-all duration-300 animate-fade-in-up ${
              plan.popular
                ? 'border-2 border-[#3A86FF] shadow-[0_4px_20px_-4px_rgba(217,119,87,0.2)] scale-105'
                : 'border border-[#E2E8F0]/40 shadow-sm'
            }`} style={{ animationDelay: `${i * 0.1}s` }}>
              <CardContent className="p-8">
                {plan.popular && (
                  <Badge className="mb-4 bg-[#3A86FF] text-white rounded-full">
                    {language === 'fr' ? 'Populaire' : 'Popular'}
                  </Badge>
                )}
                <h3 className="text-xl font-medium text-[#1E293B] mb-1" >{plan.name}</h3>
                <p className="text-sm text-[#64748B] mb-4">{plan.desc}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#1E293B]">{plan.price === '0' ? (language === 'fr' ? 'Gratuit' : 'Free') : `${plan.price}€`}</span>
                  {plan.price !== '0' && <span className="text-sm text-[#64748B]">{t('pricing.perMonth')}</span>}
                </div>
                <div className="space-y-3 mb-8">
                  {plan.features.map((f, j) => (
                    <div key={j} className="flex items-center gap-2 text-sm text-[#64748B]">
                      <Check className="w-4 h-4 text-[#00CC99] flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
                <Button
                  className={`w-full rounded-full py-5 ${
                    plan.popular
                      ? 'bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white'
                      : 'bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#1E293B]'
                  }`}
                  data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                  disabled
                >
                  {plan.current ? t('pricing.current') : t('pricing.comingSoon')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
